import type { RoutePoint, RouteStatus } from "../../../store/admin/vehicleRouteTrackingStore";
import type { ReportRoute } from "../../../store/reports/reportTrackingStore";
import type { ReportEventGroup } from "../../../store/reports/reportEventsStore";
import { resolveEventLabel } from "./eventLabels";

const DIRECTIONS = [
  "norte",
  "noreste",
  "este",
  "sureste",
  "sur",
  "suroeste",
  "oeste",
  "noroeste",
];

export const getOrientacion = (heading: number | null): string => {
  if (heading == null || isNaN(heading)) return "N/A";
  return DIRECTIONS[Math.round(heading / 45) % 8];
};

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

const getDateTimeFormatter = (tz: string): Intl.DateTimeFormat => {
  let formatter = dateTimeFormatters.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("es-ES", {
      timeZone: tz,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    dateTimeFormatters.set(tz, formatter);
  }
  return formatter;
};

export const formatDateTime = (ts: number, tz: string): string =>
  getDateTimeFormatter(tz).format(new Date(ts));

export interface HistoricRow {
  id: string;
  unidad: string;
  orden: number;
  fecha: string;
  estadoMotor: string;
  latitud: string;
  longitud: string;
  orientacion: string;
  velocidad: string;
  bateria: string;
  direccion?: string;
  lat: number;
  lng: number;
  ts: number;
  status: RouteStatus;
}

const COORD_PRECISION = 4;
const coordKey = (lat: number, lng: number): string =>
  `${lat.toFixed(COORD_PRECISION)},${lng.toFixed(COORD_PRECISION)}`;

export const buildHistoricRows = (
  routes: Pick<ReportRoute, "vehicleName" | "points">[],
  tz: string,
  addresses?: Record<string, string>,
): HistoricRow[] => {
  const rows: HistoricRow[] = [];

  routes.forEach((route) => {
    const ordered = [...route.points].sort((a, b) => b.ts - a.ts);
    ordered.forEach((p: RoutePoint, i) => {
      const row: HistoricRow = {
        id: `${route.vehicleName}-${p.ts}-${i}`,
        unidad: route.vehicleName,
        orden: i + 1,
        fecha: formatDateTime(p.ts, tz),
        estadoMotor: p.ignition ? "encendido" : "apagado",
        latitud: p.lat.toFixed(7),
        longitud: p.lng.toFixed(7),
        orientacion: getOrientacion(p.heading),
        velocidad: p.speed != null ? `${p.speed.toFixed(1)} km/h` : "N/A",
        bateria: p.battery != null ? `${p.battery.toFixed(2)} %` : "N/A",
        lat: p.lat,
        lng: p.lng,
        ts: p.ts,
        status: p.status,
      };
      if (addresses) {
        row.direccion = addresses[coordKey(p.lat, p.lng)] || "N/A";
      }
      rows.push(row);
    });
  });

  return rows;
};

export interface EventRow {
  id: string;
  deviceId: string;
  unidad: string;
  orden: number;
  fecha: string;
  evento: string;
  eventCode: number | null;
  latitud: string;
  longitud: string;
  direccion?: string;
  lat: number;
  lng: number;
  ts: number;
}

export const buildEventRows = (
  groups: Pick<ReportEventGroup, "deviceId" | "vehicleName" | "events">[],
  tz: string,
  addresses?: Record<string, string>,
): EventRow[] => {
  const rows: EventRow[] = [];

  groups.forEach((group) => {
    const ordered = [...group.events].sort((a, b) => b.ts - a.ts);
    ordered.forEach((e, i) => {
      const row: EventRow = {
        id: `${group.deviceId}-${e.ts}-${e.eventCode ?? ""}-${i}`,
        deviceId: group.deviceId,
        unidad: group.vehicleName,
        orden: i + 1,
        fecha: formatDateTime(e.ts, tz),
        evento: resolveEventLabel(e.eventCode, e.eventType),
        eventCode: e.eventCode,
        latitud: e.lat.toFixed(7),
        longitud: e.lng.toFixed(7),
        lat: e.lat,
        lng: e.lng,
        ts: e.ts,
      };
      if (addresses) {
        row.direccion = addresses[coordKey(e.lat, e.lng)] || "N/A";
      }
      rows.push(row);
    });
  });

  return rows;
};
